/* global gdAlertify */
import { useState, useEffect } from "react";
import axiosInstance from "../../../config/axiosConfig";
import { formatDate } from "../../../utils/utils";
import InfoHeader from "./InfoHeader";

const EliminarTurnoModal = ({ isOpen, onClose, onConfirm, row, periodo }) => {
    const [tipoEliminacion, setTipoEliminacion] = useState("individual"); // individual | rango
    const [tipoTurno, setTipoTurno] = useState("1");                     // 1 día | 2 noche | 3 ambos
    const [fechaInicio, setFechaInicio] = useState("");
    const [fechaFin, setFechaFin] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);

    const [periodoDesde, setPeriodoDesde] = useState("");
    const [periodoHasta, setPeriodoHasta] = useState("");

    useEffect(() => {
        if (!periodo) return;
        const [anioStr, mesStr] = periodo.split("-");
        const mes = parseInt(mesStr, 10) - 1;
        const anio = parseInt(anioStr, 10);
        const desde = new Date(anio, mes - 1, 26);
        const hasta = new Date(anio, mes, 25);
        setPeriodoDesde(desde.toISOString().split("T")[0]);
        setPeriodoHasta(hasta.toISOString().split("T")[0]);
    }, [periodo]);

    useEffect(() => {
        if (isOpen && row) {
            setTipoEliminacion("individual");
            setFechaInicio(row.fecha || "");
            setFechaFin("");
            // Determina tipoTurno inicial:
            let initialTipo = "1";
            if (row?.tipo === "day" || row?.turno === "1") initialTipo = "1";
            else if (row?.tipo === "night" || row?.turno === "2") initialTipo = "2";
            else if (["3", "daynight"].includes(String(row?.tipoTurno))) initialTipo = "3";
            setTipoTurno(initialTipo);
        }
    }, [isOpen, row]);

    if (!isOpen || !row) return null;

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validaciones
        if (tipoEliminacion === "rango") {
            if (!fechaInicio || !fechaFin) {
                gdAlertify.notiError("Debe ingresar fechas inicio y fin");
                return;
            }
            if (fechaInicio > fechaFin) {
                gdAlertify.notiError("Fecha inicio no puede ser mayor que la de fin");
                return;
            }
            if (fechaInicio < periodoDesde) {
                gdAlertify.notiError(`Fechas deben ser mayor a ${formatDate(periodoDesde)}`);
                return;
            }
        }

        gdAlertify.confirm({
            title: "Eliminar Turno(s)",
            message: "¿Estás seguro? Esta acción no se puede deshacer.",
            onConfirm: async () => {
                setIsSubmitting(true);
                gdAlertify.showLoader();
                try {
                    const payload = {
                        pacienteID: row.pacienteID,
                        auxiliarID: row.auxiliarID,
                        // Para compatibilidad con backend actual
                        data: row,
                        tipoEliminacion,                   // "individual" | "rango"
                        fechaInicio: tipoEliminacion === "rango" ? fechaInicio : row.fecha,
                        fechaFin: tipoEliminacion === "rango" ? fechaFin : row.fecha,
                        tipoTurno,                   // "1","2","3"
                        es_apoyo: row.es_apoyo,
                    };

                    const resp = await axiosInstance.post("/turnos/eliminar", payload);

                    if (!resp.data.error) {
                        gdAlertify.notiSuccess(resp.data.message || "Turno(s) eliminado(s)");
                        onConfirm({ pacienteID: row.pacienteID, fecha: row.fecha, esRango: tipoEliminacion === "rango" });
                        onClose();
                    } else {
                        gdAlertify.notiError(resp.data.message || "No se pudo eliminar");
                    }
                } catch (err) {
                    gdAlertify.notiError("Error al conectar con el servidor.");
                } finally {
                    gdAlertify.hideLoader();
                    setIsSubmitting(false);
                }
            },
            onCancel: () => gdAlertify.notify("Cancelado", "error"),
        });
    };

    return (
        <>
            <div className="modal-backdrop fade show"></div>
            <div className="modal fade show" style={{ display: "block" }} tabIndex="-1">
                <div className="modal-dialog modal-lg">
                    <div className="modal-content">
                        <form onSubmit={handleSubmit}>
                            <div className="modal-header">
                                <h5 className="modal-title">Eliminar Turno(s)</h5>
                                <button type="button" className="btn-close" onClick={onClose} disabled={isSubmitting} />
                            </div>
                            <div className="modal-body">
                                <InfoHeader row={row} />

                                <div className="mb-3">
                                    <label className="form-label">Tipo de eliminación:</label>
                                    <select
                                        className="form-select"
                                        value={tipoEliminacion}
                                        onChange={(e) => setTipoEliminacion(e.target.value)}
                                        disabled={isSubmitting}
                                    >
                                        <option value="individual">
                                            Fecha Seleccionada ({formatDate(row.fecha)} - {row.turno === "1" ? "Día" : "Noche"})
                                        </option>
                                        <option value="rango">Rango de fechas</option>
                                    </select>
                                </div>

                                {tipoEliminacion === "rango" && (
                                    <div className="row mb-3">
                                        <div className="col-md-6">
                                            <label className="form-label">Fecha Inicio:</label>
                                            <input
                                                type="date"
                                                className="form-control"
                                                value={fechaInicio}
                                                onChange={(e) => setFechaInicio(e.target.value)}
                                                min={periodoDesde}
                                                disabled={isSubmitting}
                                            />
                                        </div>
                                        <div className="col-md-6">
                                            <label className="form-label">Fecha Fin:</label>
                                            <input
                                                type="date"
                                                className="form-control"
                                                value={fechaFin}
                                                onChange={(e) => setFechaFin(e.target.value)}
                                                min={periodoDesde}
                                                disabled={isSubmitting}
                                            />
                                        </div>
                                        <p className="text-muted mt-2">
                                            Periodo válido: {formatDate(periodoDesde)} al {formatDate(periodoHasta)}
                                        </p>
                                    </div>
                                )}

                                <div className="mb-3">
                                    <label className="form-label">Tipo Turno a eliminar:</label>
                                    <select
                                        className="form-select"
                                        value={tipoTurno}
                                        onChange={(e) => setTipoTurno(e.target.value)}
                                        disabled={isSubmitting}
                                    >
                                        <option value={row.turno === "1" ? 1 : 2}>
                                            Actual - {row.turno === "1" ? "Día" : "Noche"}
                                        </option>
                                        <option value="3">Día y noche</option>
                                    </select>
                                </div>

                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={onClose} disabled={isSubmitting}>
                                    Cancelar
                                </button>
                                <button type="submit" className="btn btn-danger" disabled={isSubmitting}>
                                    {isSubmitting ? "Eliminando..." : "Eliminar"}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </>
    );
};

export default EliminarTurnoModal;
